import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  const formatCount = (count: number): string => {
    if (count >= 10000) {
      return (count / 10000).toFixed(1) + '万';
    }
    return count.toString();
  };

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group block relative rounded-lg overflow-hidden h-[400px] md:h-[480px] cursor-pointer"
      >
        <img
          src={article.imageUrl}
          alt={article.title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10"></div>
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
          <span className="inline-block px-3 py-1 text-xs font-medium bg-primary-500 text-white rounded-full mb-3 whitespace-nowrap">
            {article.category}
          </span>
          <h3 className="text-xl md:text-2xl font-heading text-white mb-2 leading-snug group-hover:text-accent-300 transition-colors">
            {article.title}
          </h3>
          <p className="text-white/70 text-sm line-clamp-2 mb-3">{article.excerpt}</p>
          <div className="flex items-center gap-4 text-white/50 text-xs">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line w-3.5 h-3.5 flex items-center justify-center"></i>
              {article.publishedAt}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line w-3.5 h-3.5 flex items-center justify-center"></i>
              {formatCount(article.viewCount)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line w-3.5 h-3.5 flex items-center justify-center"></i>
              {formatCount(article.commentCount)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
      >
        <div className="w-20 h-20 shrink-0 rounded-lg overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">
            {article.title}
          </h4>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-foreground-500">
            <span>{article.publishedAt}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line w-3 h-3 flex items-center justify-center"></i>
              {formatCount(article.viewCount)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
    >
      <div className="relative h-48 overflow-hidden">
        <img
          src={article.imageUrl}
          alt={article.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute top-3 left-3 px-2.5 py-1 text-xs font-medium bg-primary-500 text-white rounded-full whitespace-nowrap">
          {article.category}
        </span>
        {article.isHot && (
          <span className="absolute top-3 right-3 px-2 py-0.5 text-xs font-medium bg-accent-500 text-white rounded-full flex items-center gap-1 whitespace-nowrap">
            <i className="ri-fire-line w-3 h-3 flex items-center justify-center"></i>
            热门
          </span>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-heading text-base text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug mb-2">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 line-clamp-2 mb-3 leading-relaxed">
          {article.excerpt}
        </p>
        <div className="flex items-center justify-between text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-calendar-line w-3.5 h-3.5 flex items-center justify-center"></i>
            {article.publishedAt}
          </span>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line w-3.5 h-3.5 flex items-center justify-center"></i>
              {formatCount(article.viewCount)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line w-3.5 h-3.5 flex items-center justify-center"></i>
              {formatCount(article.commentCount)}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}